import { useState, useMemo } from 'react';
import { useCourtCrawls } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { SortableHeader, type SortDir } from '@/components/ui/sortable-header';
import { TablePager } from '@/components/ui/table-pager';
import { formatNumber, timeAgo, formatDuration } from '@/lib/utils';
import { History, Scale } from 'lucide-react';

const statusVariant = (status: string): 'success' | 'info' | 'warning' | 'destructive' | 'secondary' => {
  const map: Record<string, 'success' | 'info' | 'warning' | 'destructive' | 'secondary'> = {
    completed: 'success', running: 'info', pending: 'warning', failed: 'destructive',
  };
  return map[status] ?? 'secondary';
};

export default function CourtCrawlsPage() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('started_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
      setPage(1);
    }
  };

  const { data, isLoading } = useCourtCrawls({ page, sort: sortField, order: sortDir });

  const statusSummary = useMemo(() => {
    if (!data?.crawlRuns) return {};
    const counts: Record<string, number> = {};
    data.crawlRuns.forEach(r => {
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  }, [data?.crawlRuns]);

  return (
    <div className="space-y-6 animate-fade-up p-1">
      <PageBanner>
        <div className="flex items-center justify-between">
          <div className="banner-chip">
            <h1 className="text-2xl font-bold banner-title">Court Crawl Runs</h1>
          </div>
          {data && (
            <div className="banner-chip">
              <Badge variant="outline">{data.total} total runs</Badge>
            </div>
          )}
        </div>

        {Object.keys(statusSummary).length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {Object.entries(statusSummary).map(([status, count]) => (
              <div key={status} className="banner-chip">
                <Badge
                  variant={statusVariant(status)}
                  className="text-sm px-3 py-1"
                >
                  {count} {status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </PageBanner>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <span className="text-amber-700 dark:text-amber-400">All Crawl Runs</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <GlobeSpinner label="Loading court crawl runs..." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortableHeader label="Court" field="court_name" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>Jurisdiction</TableHead>
                    <TableHead>
                      <SortableHeader label="Status" field="status" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Found" field="cases_found" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="New" field="cases_new" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Errors" field="cases_errors" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Duration" field="duration_secs" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Started" field="started_at" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>Completed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.crawlRuns.map((run, idx) => (
                    <TableRow
                      key={run.id}
                      className={`${idx % 2 === 0 ? '' : 'bg-table-row-even'} ${run.status === 'failed' ? 'bg-red-50 dark:bg-red-950/20' : ''}`}
                      style={{
                        borderLeft: run.status === 'completed' ? '3px solid #34d399'
                          : run.status === 'failed' ? '3px solid #f87171'
                          : run.status === 'running' ? '3px solid #60a5fa'
                          : '3px solid transparent',
                      }}
                    >
                      <TableCell className="font-medium max-w-[200px] truncate" title={run.court_name}>
                        {run.court_name}
                      </TableCell>
                      <TableCell className="text-xs capitalize">{run.jurisdiction}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(run.status)} className="text-xs capitalize">{run.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(run.cases_found)}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(run.cases_new)}</TableCell>
                      <TableCell className="text-right">
                        {(run.cases_errors ?? 0) > 0
                          ? <Badge variant="destructive" className="text-xs">{run.cases_errors}</Badge>
                          : <span className="font-mono text-muted-foreground">0</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatDuration(run.duration_secs)}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{timeAgo(run.started_at)}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{run.completed_at ? timeAgo(run.completed_at) : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {data?.crawlRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <Scale className="h-10 w-10 text-muted-foreground/40" />
                          <p className="text-muted-foreground">No court crawl runs yet</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <TablePager
                page={page}
                onPageChange={setPage}
                total={data?.total}
                pageSize={data?.limit ?? 20}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
